import { isEmpty, last } from 'lodash-es';
import { type Dispatch, type ReactNode, type SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { VList, type VListHandle } from 'virtua';
import DocumentIcon from '@/assets/images/icons/document.svg?react';
import AudioPlayer from '@/components/common/media-content/audio-player';
import VideoViewer from '@/components/common/media-content/video-viewer';
import ProgressButton from '@/components/common/progress-button';
import { SafeImage } from '@/components/common/safe-image';
import SelectableListItem from '@/components/common/selectable-list/selectable-list-item';
import {
  ScriptType,
  transformSpecialScripts,
} from '@/components/conversations/conversation-list/message-list-item/message-content/transform-scripts';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import useDeleteBatchMessages from '@/hooks/batch-message/use-delete-batch-messages';
import {
  type BatchMessage,
  type Recipient,
  useInfiniteBatchMessages,
} from '@/hooks/batch-message/use-get-batch-messages';
import useBlacklist from '@/hooks/users/use-blacklist';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import ROUTES from '@/routes';
import { makePrompt } from '@/stores/prompter';
import { getDateFormatKey } from '@/utils/datetime';
import cn from '@/utils/merge-class-names';

export default function BatchMessagingRoute() {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  const { fetchNextPage, mapData, isLoading, isPlaceholderData } = useInfiniteBatchMessages();

  return (
    <PageLayout
      className='mb-safe-offset-4 desktop:mb-safe-offset-0 desktop:h-full'
      title={t('messaging-settings.batch-message.assistant')}
      headerConfig={{
        navigateBack: true,
        actions: !isEmpty(mapData) && !isEditing && (
          <ProgressButton
            disabled={isEmpty(mapData)}
            as='button'
            className='btn btn-ghost btn-sm text-primary mode-embedded:text-inherit'
            label='common.edit'
            onClick={() => setIsEditing(true)}
          />
        ),
      }}>
      <LoadingLayout promise={isLoading || isPlaceholderData}>
        {() => (
          <BatchMessageRecords
            // Rerender the component if there is a new message, prevent VList items from overlapping
            key={last(Object.values(mapData))?.BatchMessageId}
            onScrollToTop={fetchNextPage}
            records={Object.values(mapData)}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
          />
        )}
      </LoadingLayout>
    </PageLayout>
  );
}

function BatchMessageRecords({
  onScrollToTop,
  records,
  isEditing,
  setIsEditing,
}: {
  records: BatchMessage[];
  isEditing: boolean;
  setIsEditing: Dispatch<SetStateAction<boolean>>;
  onScrollToTop: () => void;
}) {
  const { t } = useTranslation();
  const [selectedMsgIds, setSelectedMsgIds] = useState<number[]>([]);
  const vListRef = useRef<VListHandle>(null);
  const tailId = useRef<number | null>(null);
  const { mutateAsync, isPending } = useDeleteBatchMessages();
  const navigate = useNavigate();
  const { data: blockList, isPending: isBlockListPending } = useBlacklist();

  const delayScrollUntilMediaHeightCalculated = () => {
    const id = setTimeout(() => {
      vListRef.current?.scrollToIndex(records.length - 1, { align: 'end' });
    }, 300);
    () => clearTimeout(id);
  };

  useEffect(() => {
    if (records.length > 0) {
      vListRef.current?.scrollToIndex(records.length - 1, { align: 'end' });
      tailId.current = last(records)?.BatchMessageId ?? null;
      delayScrollUntilMediaHeightCalculated();
    }
  }, []);

  useEffect(() => {
    if (records.length === 0) return;

    const currentTailId = last(records)?.BatchMessageId ?? null;
    const isUpdated = currentTailId !== tailId.current;
    if (isUpdated) {
      tailId.current = currentTailId;
      vListRef.current?.scrollToIndex(records.length - 1, { align: 'end' });

      delayScrollUntilMediaHeightCalculated();
    }
  }, [records]);

  return (
    <>
      {records.length > 0 ? (
        <VList
          onScrollEnd={() => {
            if (vListRef.current?.scrollOffset === 0) {
              onScrollToTop();
            }
          }}
          className='flex-1 px-5 mb-6'
          ref={vListRef}
          shift>
          {records.map((record, idx) => {
            const isSelected = selectedMsgIds.includes(record.BatchMessageId);

            const prevTimeTag =
              idx > 0 ? getDateFormatKey(records[idx - 1]?.CreateTime, 'collection-preview', t) : null;
            const currTimeTag = getDateFormatKey(record?.CreateTime, 'collection-preview', t);

            return (
              <SelectableListItem
                onSelected={(selected) => {
                  if (selected) {
                    setSelectedMsgIds((prev) => [...prev, record.BatchMessageId]);
                  } else {
                    setSelectedMsgIds((prev) => prev.filter((id) => id !== record.BatchMessageId));
                  }
                }}
                className='w-full gap-4'
                key={record.BatchMessageId}
                isSelected={isSelected}
                checkboxPosition='right'
                isSelectable={isEditing}>
                {prevTimeTag !== currTimeTag && (
                  <div className='text-secondary text-body2 text-center pt-4'>{currTimeTag}</div>
                )}
                <BatchMessageRecord
                  isLoading={isBlockListPending}
                  disabled={isEditing}
                  onClick={() => {
                    const targetUserIds = record.Recipients.map(({ UserId }) => UserId).filter((id) => !!id) ?? [];

                    const blockedUserIds = blockList?.map(({ BlackUserID }) => BlackUserID) ?? [];

                    const blockedSet = new Set(blockedUserIds);

                    const areAllReceiversBlocked =
                      !!targetUserIds.length &&
                      !!blockedUserIds.length &&
                      targetUserIds.every((id) => blockedSet.has(id));

                    if (areAllReceiversBlocked) {
                      return makePrompt({
                        key: 'kick-member',
                        type: 'alert',
                        title: t('messaging-settings.batch-message.not-support-send-to-another'),
                        resolveButtonLabel: t('common.i-got-it'),
                      }).catch(console.warn);
                    }

                    navigate(
                      ROUTES.MY.SETTINGS.MESSAGING.BATCH.CREATE.BY_ID.$buildPath({
                        params: { batchId: record.BatchMessageId },
                      }),
                    );
                  }}
                  id={record.BatchMessageId}
                  message={record.Message}
                  type={record.MsgType}
                  recipients={record.Recipients}
                />
              </SelectableListItem>
            );
          })}
        </VList>
      ) : (
        <div className='flex-1 w-full flex items-center justify-center'>
          <p className='text-secondary text-body1'>{t('messaging-settings.batch-message.no-data')}</p>
        </div>
      )}
      <div className='px-5 flex gap-2 '>
        {isEditing ? (
          <>
            <button
              type='button'
              className='btn btn-ghost border-neutral-200 flex-1 font-normal'
              onClick={() => {
                setIsEditing(false);
                setSelectedMsgIds([]);
              }}>
              {t('common.cancel')}
            </button>
            <ProgressButton
              isLoading={isPending}
              onClick={() => {
                mutateAsync(selectedMsgIds).then(() => {
                  setSelectedMsgIds([]);
                  setIsEditing(false);
                });
              }}
              as='button'
              disabled={selectedMsgIds.length < 1}
              className='btn btn-error  bg-red-600 flex-1 text-white font-normal'>
              {t('common.delete')}({`${selectedMsgIds.length}`})
            </ProgressButton>
          </>
        ) : (
          <ProgressButton
            as='button'
            onClick={() => navigate(ROUTES.MY.SETTINGS.MESSAGING.BATCH.CREATE.BY_ID.$path())}
            className='btn-primary w-full font-normal'
            label='messaging-settings.batch-message.create'
          />
        )}
      </div>
    </>
  );
}
type Props = {
  isLoading: boolean;
  id: number;
  message: string;
  type: ConversationMessageContentType;
  recipients: Recipient[];
  className?: string;
  onClick: () => void;
  disabled: boolean;
};

function BatchMessageRecord(props: Props) {
  const { t } = useTranslation();
  const recipients = useMemo(
    () =>
      props.recipients
        .filter((r) => r.RecipientName)
        .map((r) => r.RecipientName)
        .join(','),
    [props.recipients],
  );

  let component: ReactNode;

  switch (props.type) {
    case ConversationMessageContentType.Audio:
      component = (
        <div className='bg-white rounded-full w-fit m-auto'>
          <AudioPlayer
            src={props.message}
            className='px-4 py-1.5 '
          />
        </div>
      );
      break;
    case ConversationMessageContentType.File: {
      const [_url, name] = props.message.split('|');
      component = (
        <div className='cursor-pointer mx-auto flex h-full max-w-full items-center gap-2 rounded-md px-2 py-1 group-data-sender:theme-ryuryu:bg-blue-300 group-data-sender:theme-wangyou:bg-teal-200 group-data-sender:theme-threetwoone:bg-purple-100 group-data-sender:theme-eightk:bg-purple-100 group-data-sender:theme-bonglan:bg-red-300 group-data-sender:theme-kbar:bg-red-300  group-data-sender:text-body bg-base-100 text-body'>
          <div className='relative flex min-w-0 max-w-full flex-1 items-center gap-2'>
            <DocumentIcon className='size-6' />
            <p className='font-mono line-clamp-2 break-all text-body1 '>{name}</p>
          </div>
        </div>
      );
      break;
    }

    case ConversationMessageContentType.Emoticon:
      component = (
        <SafeImage
          className='size-16 bg-cover bg-transparent'
          alt='sticker message'
          src={props.message}
          size={[64, 64]}
        />
      );
      break;
    case ConversationMessageContentType.Image:
      component = (
        <SafeImage
          displayType='img'
          className='max-h-60 cursor-pointer'
          alt='media content'
          src={props.message}
        />
      );
      break;
    case ConversationMessageContentType.Text:
      component = (
        <h3 className='text-h3 text-left text-wrap whitespace-pre-wrap w-full line-clamp-3'>
          {transformSpecialScripts(
            props.id,
            props.message,
            [],
            [ScriptType.LineBreak, ScriptType.Link, ScriptType.Emoticon],
          )}
        </h3>
      );
      break;
    case ConversationMessageContentType.Video:
      component = (
        <VideoViewer
          src={props.message}
          autoPlay={false}
        />
      );
      break;
  }

  return (
    <>
      <div className={cn('bg-base-100 p-4 mt-2 flex flex-col gap-4 rounded w-full', props.className)}>
        {[ConversationMessageContentType.Audio, ConversationMessageContentType.Emoticon].includes(props.type) ? (
          component
        ) : (
          <Link
            to={{ pathname: ROUTES.MY.SETTINGS.MESSAGING.BATCH.PREVIEW.$buildPath({ params: { batchId: props.id } }) }}
            className={cn({ 'm-auto': props.type !== ConversationMessageContentType.Text })}>
            {component}
          </Link>
        )}
        <hr />
        <div className='flex flex-row justify-between gap-2 text-left'>
          <div>
            <p className='text-body2'>{t('messaging-settings.batch-message.sent-to')}</p>
            <p className='text-body2 break-all line-clamp-1'>{recipients}</p>
          </div>
          <ProgressButton
            as='button'
            isLoading={props.isLoading}
            disabled={props.disabled}
            className='btn btn-primary btn-md font-normal'
            onClick={props.onClick}>
            {t('messaging-settings.batch-message.send-another-one')}
          </ProgressButton>
        </div>
      </div>
    </>
  );
}
